import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { updateAddressStatus } from '@/hooks/useAddressValidation';
import type { DmpAddressAttributes } from '@uniticket/shared';

// « Corriger le statut seul » — CONSOLE DMP UNIQUEMENT, et action d'EXCEPTION.
//
// La règle arrêtée avec le user le 2026-08-31 : le statut se DÉDUIT des actions. Release l'écrit avec
// neuf autres colonnes, Un-release fait l'inverse. Ce bloc ne conteste pas cette règle, il couvre le
// trou qu'elle laisse : aucune action de l'app ne sait poser ni retirer HOMES_CONNECT (52 509 adresses
// en production), donc une adresse à tort dans cet état n'aurait jamais pu être corrigée autrement
// qu'en SQL direct — sans trace. Ici, tout passe par le journal d'actions.
//
// Trois valeurs seulement : c'est la CHECK du DMP qui les impose
// (enum_3a1708f12a13488abc5313c0c25dcb95), relevée le 31-08. Une faute de frappe est donc impossible ;
// le risque est de choisir la mauvaise des trois, et c'est lui qu'on adresse par les avertissements.
//
// Replié par défaut : une action d'exception ne doit pas s'offrir aussi facilement qu'un Release.

const STATUSES = ['NOT_AVAILABLE', 'HOMES_PASSED', 'HOMES_CONNECT'] as const;
type Status = (typeof STATUSES)[number];

export function AddressStatusFix({
  jvid, attributes, onDone,
}: { jvid: number; attributes: DmpAddressAttributes | null; onDone: () => void }) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState<Status | ''>('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  if (!attributes) return null;

  const current = attributes.status as Status | null;
  const published = attributes.approvedForRelease || attributes.homeReleased;

  // Les deux avertissements, calculés sur le choix EN COURS — ils apparaissent avant de valider, pas
  // après. On n'interdit rien : on nomme la conséquence et on renvoie vers le bon outil.
  //
  // 1. Contredire les drapeaux de publication, c'est refabriquer l'état à moitié publié que le user a
  //    rencontré le 31-08 (adresse « officiellement released » absente du service file).
  const contradicts = published && next === 'NOT_AVAILABLE';
  // 2. HOMES_CONNECT n'est pas une étiquette mais une GARDE : bandeau « client raccordé », refus du
  //    retrait de scope list, refus du dé-release. En sortir désarme les trois d'un coup.
  const leavingConnect = current === 'HOMES_CONNECT' && next !== '' && next !== 'HOMES_CONNECT';
  const enteringConnect = current !== 'HOMES_CONNECT' && next === 'HOMES_CONNECT';

  const canSave = next !== '' && next !== current && reason.trim().length >= 3 && !saving;

  async function save() {
    if (!canSave) return;
    if (!confirmDmpWrite(`${t('tickets:dmpLookup.statusFix.action')} → ${next} — JVID ${jvid}`)) return;
    setSaving(true);
    try {
      await updateAddressStatus(jvid, next, reason.trim());
      toast.success(t('tickets:dmpLookup.statusFix.done', { status: next }));
      setOpen(false); setNext(''); setReason('');
      onDone();
    } catch (err) {
      toast.error((err as Error)?.message || t('tickets:dmpLookup.statusFix.error'));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-500 hover:text-slate-700"
      >
        <ShieldAlert className="h-3.5 w-3.5" />
        {t('tickets:dmpLookup.statusFix.open')}
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-300 bg-slate-50/70 px-3 py-2.5">
      <p className="text-xs font-semibold text-slate-800">{t('tickets:dmpLookup.statusFix.title')}</p>
      <p className="mt-0.5 text-[11px] text-slate-500">{t('tickets:dmpLookup.statusFix.hint')}</p>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setNext(s === next ? '' : s)}
            disabled={s === current || saving}
            className={`rounded-md border px-2 py-1 text-[11px] font-mono font-semibold transition-colors
              ${s === current ? 'cursor-default border-slate-200 bg-slate-100 text-slate-400'
                : s === next ? 'border-brand-rose bg-brand-rose/10 text-brand-rose'
                : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
          >
            {s}
            {s === current && <span className="ml-1 font-sans font-normal">{t('tickets:dmpLookup.statusFix.currentTag')}</span>}
          </button>
        ))}
      </div>

      {(contradicts || leavingConnect || enteringConnect) && (
        <div className="mt-2 space-y-1.5">
          {contradicts && (
            <p className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
              <AlertTriangle className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              {t('tickets:dmpLookup.statusFix.warnContradicts')}
            </p>
          )}
          {leavingConnect && (
            <p className="flex items-start gap-1.5 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-[11px] text-red-900">
              <AlertTriangle className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              {t('tickets:dmpLookup.statusFix.warnLeavingConnect')}
            </p>
          )}
          {enteringConnect && (
            <p className="flex items-start gap-1.5 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-[11px] text-red-900">
              <AlertTriangle className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              {t('tickets:dmpLookup.statusFix.warnEnteringConnect')}
            </p>
          )}
        </div>
      )}

      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('tickets:dmpLookup.statusFix.reasonPlaceholder')}
        maxLength={500}
        disabled={saving}
        className="mt-2 h-8 text-xs"
      />
      <p className="mt-0.5 text-[10px] text-slate-400">{t('tickets:dmpLookup.statusFix.reasonHint')}</p>

      <div className="mt-2 flex gap-2">
        <Button size="sm" className="h-7 text-xs" disabled={!canSave} onClick={() => void save()}>
          {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {t('tickets:dmpLookup.statusFix.action')}
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={saving}
          onClick={() => { setOpen(false); setNext(''); setReason(''); }}>
          {t('tickets:dmpLookup.statusFix.cancel')}
        </Button>
      </div>
    </div>
  );
}
